import { CHAT_V3_API_BASE_URL } from '../constants/api';
import { firestore } from '../firebaseHelper';
import {
    CHAT_V2_MAX_INPUT_CHARACTERS,
    chatV2Request,
    isChatV2RunActive,
    normalizeChatV2RunEnvelope,
    normalizeChatV2ThreadResponse,
} from './chatV2';
import { validatePublicChatExamplesManifest } from '../utils/publicChatExamples';

export const CHAT_V3_PREVIEW_PRESENTATION_VERSION = 2;

const request = (options) => chatV2Request({ ...options, baseURL: CHAT_V3_API_BASE_URL });
const mutate = (path, data, options = {}) => request({
    method: 'post', url: `/chat_v3/${path}`, data, ...options,
});

const input = (text) => {
    const value = String(text || '').trim();
    if (!value || value.length > CHAT_V2_MAX_INPUT_CHARACTERS) {
        throw new Error('Enter a message of up to 2,000 characters.');
    }
    return value;
};

// One key per user submission. Network errors never retry against another engine.
const requestId = () => window.crypto?.randomUUID?.() ||
    `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const startChatV3Thread = async (text) => {
    const run = normalizeChatV2RunEnvelope(await mutate('start', {
        text: input(text),
        requestId: requestId(),
        version: CHAT_V3_PREVIEW_PRESENTATION_VERSION,
        includeEarlyShowMatch: true,
    }, { requiresAppCheck: true }));

    if (run?.engineVersion === 3 && isChatV2RunActive(run) && !run.earlyShowMatch &&
        [run.threadId, run.runId].every((id) => typeof id === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(id))) {
        // Only new submissions launch this optional request. Returning the run
        // immediately lets navigation and live status proceed independently.
        // The server owns the one-time claim; failure leaves normal chat intact.
        mutate('early_show_lookup', { threadId: run.threadId, runId: run.runId },
            { requiresAppCheck: true }).catch(() => {});
    }
    return run;
};
export const sendChatV3Message = async (threadId, text) => normalizeChatV2RunEnvelope(await mutate(
    'message', {
        threadId,
        text: input(text),
        requestId: requestId(),
        version: CHAT_V3_PREVIEW_PRESENTATION_VERSION,
    }, { requiresAppCheck: true },
));
export const getChatV3Thread = async (threadId) => normalizeChatV2ThreadResponse(await request({
    method: 'get', url: `/chat_v3/thread/${encodeURIComponent(threadId)}`,
}));
export const getSharedChatV3Thread = async (threadId) => normalizeChatV2ThreadResponse(await request({
    method: 'get', url: `/chat_v3/public_thread/${encodeURIComponent(threadId)}`, allowAnonymous: false,
}));
export const getWebChatThreads = ({ cursor = null, limit = 20 } = {}) => mutate(
    'history', { ...(cursor ? { cursor } : {}), limit }, { allowAnonymous: false },
);
export const shareChatV3Thread = (threadId) => mutate('share', { threadId });
export const unshareChatV3Thread = (threadId) => mutate('unshare', { threadId });
export const deleteChatV3Thread = (threadId) => mutate('delete', { threadId }, { allowAnonymous: false });
export const getChatV3RunPublicStatusRef = (threadId, runId) => firestore
    .collection('chat_v3_threads').doc(threadId)
    .collection('runs').doc(runId).collection('public_status').doc('current');

export const getWebPublicChatExamples = async () => validatePublicChatExamplesManifest(await request({
    method: 'get', url: '/chat_v3/public_examples', includeAuthorization: false, timeout: 15000,
}));
export const getWebChatSuggestions = () => mutate('suggestions', {}, { allowAnonymous: false });
export const deleteWebChatSuggestion = (suggestion) => mutate('suggestion_delete', typeof suggestion === 'string' ? { text: suggestion } : {
    publicExampleKey: suggestion.publicExampleKey,
    publicExamplesRevision: suggestion.publicExamplesRevision,
}, { allowAnonymous: false });
